/**
 * useDragResize — pointer-based drag-to-resize with live size snapping.
 *
 * The SE-corner handle (bottom-right of tile in edit mode) lets users drag to
 * resize tiles. While dragging, a live preview shows the nearest allowed size.
 * On release, commits via onCommit(tileId, nearestSize).
 *
 * Pure math: pointer delta → candidate size → nearest allowed size.
 * Immutability: all state transitions replace state wholesale.
 * glance-grammar-spec §5.1: direct manipulation is allowed motion.
 */
import { useState, useCallback, useRef } from 'react';
import type { TileSize } from 'briefpane/schema';
import { sizeToWH } from 'briefpane/schema';

export interface DragResizeState {
  readonly isResizing: boolean;
  readonly resizeTileId: string | null;
  /** The snapped size while dragging (live preview). */
  readonly previewSize: TileSize | null;
  /** Whether the preview size would cause a collision or go out of bounds (invalid). */
  readonly previewInvalid: boolean;
}

export interface DragResizeHandlers {
  readonly state: DragResizeState;
  /** Call from the SE-corner resize handle's onPointerDown. */
  readonly onResizeStart: (
    tileId: string,
    currentSize: TileSize,
    allowedSizes: readonly TileSize[],
    e: React.PointerEvent,
  ) => void;
  /** Attach to the grid container. */
  readonly onPointerMove: (e: React.PointerEvent) => void;
  readonly onPointerUp: (e: React.PointerEvent) => void;
  readonly onPointerCancel: () => void;
}

const IDLE: DragResizeState = {
  isResizing: false,
  resizeTileId: null,
  previewSize: null,
  previewInvalid: false,
};

/**
 * snapToAllowedSize — pure math: pointer delta → nearest allowed size.
 *
 * @param dx - pointer X delta from drag start in pixels
 * @param dy - pointer Y delta from drag start in pixels
 * @param currentSize - tile size at drag start
 * @param allowedSizes - registry allowed sizes for this tile type
 * @param cellWidth - grid cell width in pixels (including gap)
 * @param cellHeight - grid cell height in pixels (including gap)
 * @returns the nearest allowed TileSize to the pointer position
 */
export function snapToAllowedSize(
  dx: number,
  dy: number,
  currentSize: TileSize,
  allowedSizes: readonly TileSize[],
  cellWidth: number,
  cellHeight: number,
): TileSize {
  const { w: currentW, h: currentH } = sizeToWH(currentSize);
  // Candidate dimensions from pointer delta
  const candidateW = Math.max(1, currentW + Math.round(dx / cellWidth));
  const candidateH = Math.max(1, currentH + Math.round(dy / cellHeight));

  // Find nearest allowed size by Manhattan distance in grid cells
  let nearest: TileSize = allowedSizes[0] ?? currentSize;
  let nearestDist = Infinity;
  for (const size of allowedSizes) {
    const { w: sw, h: sh } = sizeToWH(size);
    const dist = Math.abs(sw - candidateW) + Math.abs(sh - candidateH);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = size;
    }
  }
  return nearest;
}

export function useDragResize(
  cellWidth: number,
  cellHeight: number,
  checkCollision: (tileId: string, size: TileSize) => boolean,
  onCommit: (tileId: string, size: TileSize) => void,
): DragResizeHandlers {
  const [state, setState] = useState<DragResizeState>(IDLE);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startSize: TileSize;
    allowedSizes: readonly TileSize[];
    tileId: string;
  } | null>(null);

  const onResizeStart = useCallback(
    (
      tileId: string,
      currentSize: TileSize,
      allowedSizes: readonly TileSize[],
      e: React.PointerEvent,
    ) => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startSize: currentSize,
        allowedSizes,
        tileId,
      };
      setState({
        isResizing: true,
        resizeTileId: tileId,
        previewSize: currentSize,
        previewInvalid: false,
      });
    },
    [],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const { startX, startY, startSize, allowedSizes, tileId } = dragRef.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const nearest = snapToAllowedSize(dx, dy, startSize, allowedSizes, cellWidth, cellHeight);
      const invalid = checkCollision(tileId, nearest);
      setState((prev) => ({ ...prev, previewSize: nearest, previewInvalid: invalid }));
    },
    [cellWidth, cellHeight, checkCollision],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const { startX, startY, startSize, allowedSizes, tileId } = dragRef.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const finalSize = snapToAllowedSize(dx, dy, startSize, allowedSizes, cellWidth, cellHeight);
      const invalid = checkCollision(tileId, finalSize);
      dragRef.current = null;
      setState(IDLE);
      if (!invalid) {
        onCommit(tileId, finalSize);
      }
      // If invalid: revert calmly — no commit, no jarring animation.
    },
    [cellWidth, cellHeight, checkCollision, onCommit],
  );

  const onPointerCancel = useCallback(() => {
    dragRef.current = null;
    setState(IDLE);
  }, []);

  return { state, onResizeStart, onPointerMove, onPointerUp, onPointerCancel };
}
